/**
 * Hook to check oncall assignment conflicts when creating/updating dayoff requests.
 * Watches day_off_range_time changes and queries the oncall conflict API.
 */
import { useMemo } from 'react'
import dayjs from 'dayjs'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    checkOncallConflict,
    OncallConflictResult,
} from '@/modules/Oncall/services/assignments'
import { DayOffRangeTime } from '../types/payload'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface UseOncallConflictCheckParams {
    employeeId: number | undefined
    rangeTime: DayOffRangeTime[]
}

export const useOncallConflictCheck = ({
    employeeId,
    rangeTime,
}: UseOncallConflictCheckParams) => {
    // Derive start_date and end_date from range time entries
    const dateRange = useMemo(() => {
        if (!rangeTime || rangeTime.length === 0) return null

        const dates = rangeTime
            .map((item) => item.start_day_off)
            .filter(Boolean)
            .sort()

        if (dates.length === 0) return null

        return {
            start_date: dates[0],
            end_date: dates[dates.length - 1],
        }
    }, [rangeTime])

    const enabled = !!employeeId && !!dateRange

    const { data, isFetching } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.ONCALL_ASSIGNMENTS,
            'conflict-check',
            employeeId,
            dateRange?.start_date,
            dateRange?.end_date,
        ],
        queryFn: async () => {
            try {
                const result = await checkOncallConflict({
                    employee_id: employeeId!,
                    start_date: dateRange!.start_date,
                    end_date: dateRange!.end_date,
                })
                return { result } as { result: OncallConflictResult }
            } catch {
                return { result: null } as { result: null }
            }
        },
        enabled,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
    })

    const conflictData = data?.result ?? null

    // Format conflict dates for display
    const conflictDatesFormatted = useMemo(() => {
        if (!conflictData?.conflict_dates) return []
        return conflictData.conflict_dates.map((date) =>
            dayjs(date).format('DD/MM/YYYY')
        )
    }, [conflictData])

    return {
        hasConflict: conflictData?.has_conflict ?? false,
        conflictCount: conflictData?.conflict_count ?? 0,
        conflictDates: conflictData?.conflict_dates ?? [],
        conflictDatesFormatted,
        assignments: conflictData?.assignments ?? [],
        isChecking: isFetching,
    }
}
